import {
  IdempotencyConflictError,
  SlotUnavailableError,
  StaleSnapshotError,
  type CrmPort,
} from "../integrations/crm";
import { evaluateBookingMutation } from "./mutation-guard";
import { failedOutcome } from "./outcomes";
import type {
  AgentRequest,
  AgentRunResult,
  BookAppointmentCall,
  BookingToolResult,
  GuardReason,
  Planner,
  ToolResult,
  TraceEvent,
  TraceStatus,
} from "./types";

export interface AgentRuntimeOptions {
  maxSteps?: number;
  clock?: () => Date;
  beforeGuard?: (
    call: BookAppointmentCall,
    crm: CrmPort,
  ) => Promise<void> | void;
}

function adapterFailureReason(error: unknown): GuardReason | "adapter_rejected_mutation" {
  if (error instanceof StaleSnapshotError) return "stale_snapshot";
  if (error instanceof SlotUnavailableError) return "slot_not_available";
  if (error instanceof IdempotencyConflictError) return "idempotency_conflict";
  return "adapter_rejected_mutation";
}

export class AgentRuntime {
  private readonly maxSteps: number;
  private readonly clock: () => Date;

  constructor(
    private readonly planner: Planner,
    private readonly crm: CrmPort,
    private readonly options: AgentRuntimeOptions = {},
  ) {
    this.maxSteps = options.maxSteps ?? 6;
    this.clock = options.clock ?? (() => new Date());
  }

  async run(request: AgentRequest): Promise<AgentRunResult> {
    const trace: TraceEvent[] = [];
    const toolResults: ToolResult[] = [];

    const addTrace = (
      stage: TraceEvent["stage"],
      status: TraceStatus,
      label: string,
      data?: TraceEvent["data"],
    ): void => {
      trace.push({
        sequence: trace.length + 1,
        at: this.clock().toISOString(),
        stage,
        status,
        label,
        ...(data ? { data } : {}),
      });
    };

    addTrace("request", "info", "Synthetic request accepted", {
      sessionId: request.sessionId,
      requestedSlot: request.requestedSlot ?? null,
      hasIdempotencyKey: Boolean(request.idempotencyKey),
      hasStructuredConfirmation: Boolean(request.confirmation),
    });

    if (!request.sessionId.trim() || !request.message.trim()) {
      const outcome = failedOutcome("invalid_request");
      addTrace("outcome", "error", "Request failed validation", {
        code: outcome.code,
      });
      return {
        reply: "The synthetic request is missing a session or message.",
        outcome,
        trace,
      };
    }

    for (let step = 1; step <= this.maxSteps; step += 1) {
      addTrace("planner", "pending", "Planner evaluated current state", {
        step,
        priorToolResults: toolResults.length,
      });

      const action = await this.planner.next({
        request,
        toolResults,
      });

      if (action.type === "respond") {
        addTrace("outcome", "success", "Typed outcome produced", {
          kind: action.outcome.kind,
        });
        return {
          reply: action.reply,
          outcome: action.outcome,
          trace,
        };
      }

      addTrace("tool", "pending", "Planner proposed a tool call", {
        callId: action.call.id,
        tool: action.call.name,
      });

      if (action.call.name === "find_slots") {
        const snapshot = await this.crm.getAvailability();
        toolResults.push({
          callId: action.call.id,
          name: "find_slots",
          status: "success",
          snapshot,
        });
        addTrace("tool", "success", "Read-only tool completed", {
          callId: action.call.id,
          snapshotVersion: snapshot.version,
          availableSlots: snapshot.slots,
        });
        continue;
      }

      await this.options.beforeGuard?.(action.call, this.crm);
      const decision = await evaluateBookingMutation(
        request,
        action.call,
        this.crm,
      );

      addTrace(
        "guard",
        decision.allowed ? "allowed" : "blocked",
        decision.allowed
          ? "Deterministic guard allowed the action"
          : "Deterministic guard blocked the action",
        decision.allowed
          ? {
              callId: action.call.id,
              replay: decision.replay,
            }
          : {
              callId: action.call.id,
              reason: decision.reason,
            },
      );

      if (!decision.allowed) {
        toolResults.push({
          callId: action.call.id,
          name: "book_appointment",
          status: "blocked",
          reason: decision.reason,
        });
        addTrace("tool", "blocked", "Mutation tool was not executed", {
          callId: action.call.id,
          reason: decision.reason,
        });
        continue;
      }

      try {
        const committed = await this.crm.bookAppointment(action.call.input);
        const status: BookingToolResult["status"] = committed.replayed
          ? "replayed"
          : "booked";
        toolResults.push({
          callId: action.call.id,
          name: "book_appointment",
          status,
          booking: committed.booking,
        });
        addTrace(
          "mutation",
          committed.replayed ? "replayed" : "success",
          committed.replayed
            ? "Existing mutation result returned"
            : "Mock CRM mutation committed",
          {
            callId: action.call.id,
            bookingId: committed.booking.bookingId,
            slot: committed.booking.slot,
          },
        );
      } catch (error) {
        const reason = adapterFailureReason(error);
        toolResults.push({
          callId: action.call.id,
          name: "book_appointment",
          status: "failed",
          reason,
        });
        addTrace("mutation", "error", "Adapter rejected the mutation", {
          callId: action.call.id,
          reason,
        });
      }
    }

    const outcome = failedOutcome("planner_exhausted");
    addTrace("outcome", "error", "Planner step limit reached", {
      code: outcome.code,
    });
    return {
      reply: "The synthetic planner reached its bounded step limit.",
      outcome,
      trace,
    };
  }
}
